/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    this.clearDeliveryAttributesIfCartEmpty();
    this.initDeliveryOptions();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);

    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.initDeliveryOptions();
    });

    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.clearDeliveryAttributesIfCartEmpty();
      this.initDeliveryOptions();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  /**
   * Clear cart-wide delivery attributes when the cart becomes empty.
   */
  async clearDeliveryAttributesIfCartEmpty() {
    const hasItems =
      this.querySelectorAll('[data-merge-list-item]').length > 0;

    if (hasItems || this.dataset.deliveryEmptyCleared === 'true') {
      return;
    }

    this.dataset.deliveryEmptyCleared = 'true';

    await this.saveDeliveryAttributes(
      {
        delivery_date: '',
        delivery_time: ''
      },
      null
    );
  }

  /**
   * Initialize cart-wide delivery date/time controls.
   */
  async initDeliveryOptions() {
    const container = this.querySelector('[data-delivery-options]');

    if (container) {
      delete this.dataset.deliveryEmptyCleared;
    }

    if (!container || container.dataset.deliveryInitialized === 'true') {
      return;
    }

    container.dataset.deliveryInitialized = 'true';

    const mode = container.dataset.mode || 'normal';
    const dateSelect = container.querySelector('[data-delivery-date]');
    const timeSelect = container.querySelector('[data-delivery-time]');
    const status = container.querySelector('[data-delivery-status]');

    const currentDate = container.dataset.currentDate || '';
    const currentTime = container.dataset.currentTime || '';

    if (timeSelect) {
      timeSelect.addEventListener('change', async () => {
        await this.saveDeliveryAttributes(
          { delivery_time: timeSelect.value },
          status
        );

        container.dataset.currentTime = timeSelect.value;
      });
    }

    if (mode === 'blocked') {
      if (currentDate || currentTime) {
        const saved = await this.saveDeliveryAttributes(
          {
            delivery_date: '',
            delivery_time: ''
          },
          status
        );

        if (saved) {
          container.dataset.currentDate = '';
          container.dataset.currentTime = '';

          if (timeSelect) {
            timeSelect.value = '';
          }
        }
      }

      return;
    }

    if (mode === 'preorder') {
      if (currentDate) {
        const saved = await this.saveDeliveryAttributes(
          {
            delivery_date: ''
          },
          status
        );

        if (saved) {
          container.dataset.currentDate = '';
        }
      }

      return;
    }

    if (!dateSelect) {
      return;
    }

    dateSelect.disabled = true;

    try {
      const response = await fetch(
        'https://tovo-delivery.take-f00.workers.dev/cart-delivery-options',
        {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data.ok || !Array.isArray(data.selectable_dates)) {
        throw new Error('Invalid delivery options response');
      }

      const existing =
        dateSelect.value ||
        currentDate;

      dateSelect.innerHTML =
        '<option value="">指定なし</option>';

      data.selectable_dates.forEach((item) => {
        const option = document.createElement('option');

        option.value =
          item.date.replaceAll('-', '/');

        option.textContent =
          item.display;

        if (option.value === existing) {
          option.selected = true;
        }

        dateSelect.appendChild(option);
      });

      dateSelect.disabled = false;

      dateSelect.addEventListener('change', async () => {
        await this.saveDeliveryAttributes(
          {
            delivery_date: dateSelect.value
          },
          status
        );

        container.dataset.currentDate =
          dateSelect.value;
      });
    } catch (error) {
      console.error(
        'Delivery options load failed:',
        error
      );

      if (status) {
        status.textContent =
          'お届け希望日の取得に失敗しました。時間をおいて再度お試しください。';
      }
    }
  }

  /**
   * Save cart attributes without reloading the drawer.
   * @param {object} attributes - Attributes to save.
   * @param {HTMLElement|null} status - Optional status element.
   * @returns {Promise<boolean>}
   */
  async saveDeliveryAttributes(
    attributes,
    status
  ) {
    if (status) {
      status.textContent =
        '保存中…';
    }

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`
        );
      }

      if (status) {
        status.textContent = '';
      }

      return true;
    } catch (error) {
      console.error(
        'Delivery attributes save failed:',
        error
      );

      if (status) {
        status.textContent =
          '保存に失敗しました。もう一度お試しください。';
      }

      return false;
    }
  }
}

window.customElements.define(
  'cart-drawer',
  CartDrawer
);